import React from 'react';
import { useState } from 'react';
import { Gallery } from 'iconsax-react';
import { Skeleton } from 'antd';
import { SellerProductBasicDetails } from './types';
import { useTranslation } from 'react-i18next';
import TitleText from '../../atoms/TitleText';

type Props = {
  basicDetails?: SellerProductBasicDetails;
  loading?: boolean;
};

export default function ProductImagesGallery({ basicDetails, loading }: Props) {
  const { t } = useTranslation();
  const [activeImageUrl, setActiveImageUrl] = useState<string | null>(null);

  const sortedImages = [...(basicDetails?.productImages ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder
  );

  const currentImage =
    activeImageUrl ||
    sortedImages.find((e) => e.isThumbnail)?.url ||
    sortedImages[0]?.url ||
    null;

  return (
    <div
      className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800"
      data-testid="seller-product-images-gallery"
    >
      <div
        className="flex items-center gap-2.5 mb-4"
        data-testid="seller-product-images-header"
      >
        <TitleText
          text={t('components.productDetailsPage.images')}
          size="xl"
          className="!font-bold !text-base"
        />
      </div>

      {/* Main image */}
      <div
        className="bg-gray-50 dark:bg-gray-900 rounded-xl aspect-square relative mb-4 flex items-center justify-center border border-gray-100 dark:border-gray-800 overflow-hidden"
        data-testid="seller-product-image-main-container"
      >
        <div
          className="relative w-full h-full "
          data-testid="seller-product-image-main-inner"
        >
          {loading ? (
            <Skeleton.Image active className="!w-full !h-full" />
          ) : currentImage ? (
            <img
              src={currentImage}
              alt="Product"
              className="h-full w-full object-contain p-4"
              data-testid="seller-product-image-main-img"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              data-testid="seller-product-image-empty"
            >
              <Gallery size={48} className="text-gray-400" />
            </div>
          )}
        </div>
      </div>

      {/* Thumbnail grid */}
      <div
        className="grid grid-cols-4 gap-3"
        data-testid="seller-product-thumbnails-grid"
      >
        {loading
          ? Array(4)
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  data-testid={`seller-product-thumbnail-skeleton-${i}`}
                >
                  <Skeleton.Avatar active size={90} shape="square" />
                </div>
              ))
          : sortedImages.map((img, idx) => (
              <div
                key={img.id}
                onClick={() => setActiveImageUrl(img.url)}
                className={`rounded-[12px] overflow-hidden aspect-square relative cursor-pointer transition-colors border-2 ${
                  currentImage === img.url
                    ? 'border-brand-primary'
                    : 'border-transparent hover:border-gray-300'
                }`}
                data-testid={`seller-product-thumbnail-container-${idx}`}
              >
                <img
                  src={img.url}
                  alt={img.name}
                  className="h-full w-full object-cover"
                  data-testid={`seller-product-thumbnail-img-${idx}`}
                />
              </div>
            ))}
      </div>
    </div>
  );
}
